import { useRef } from 'react';
import { useModal } from '../useModal';
import { pbToast } from '../toast';
import { validateChartDoc } from '../orgChartApi';
import ZoomControls from './ZoomControls';
import type { OrgChartDoc, Selection, Person } from '../types';

type ZoomLevel = 'fit' | '0.5' | '1';

function progLabel(doc: OrgChartDoc, gi: number, pi: number): string {
  const group = doc.groups[gi];
  if (!group) return '';
  const program = group.programs[pi];
  return group.programs.length === 1 ? group.short : `${group.short} · ${program ? program.short : ''}`;
}

function personAt(doc: OrgChartDoc, selection: Selection): Person | null {
  if (!selection || selection.kind !== 'person') return null;
  const group = doc.groups[selection.gi];
  const program = group && group.programs[selection.pi];
  return program ? program.people[selection.ki] || null : null;
}

const NOOP = () => {};

interface OrgChartToolbarProps {
  doc: OrgChartDoc;
  selection: Selection;
  editMode: boolean;
  zoom: ZoomLevel;
  canUndo: boolean;
  svgRef: React.RefObject<SVGSVGElement | null>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: { type: string; [key: string]: any }) => void;
  baseline: OrgChartDoc;
  onAddPerson?: (selection: Selection) => void;
  onAddProgram?: (gi: number) => void;
  onAddGroup?: () => void;
  onEditPerson?: (selection: Selection) => void;
  onEditProgram?: (selection: Selection) => void;
  onEditGroup?: (selection: Selection) => void;
  onEditLeadership?: () => void;
  onSettings?: () => void;
}

export default function OrgChartToolbar({
  doc,
  selection,
  editMode,
  zoom,
  canUndo,
  svgRef,
  dispatch,
  baseline,
  onAddPerson = NOOP,
  onAddProgram = NOOP,
  onAddGroup = NOOP,
  onEditPerson = NOOP,
  onEditProgram = NOOP,
  onEditGroup = NOOP,
  onEditLeadership = NOOP,
  onSettings = NOOP,
}: OrgChartToolbarProps) {
  const { confirm } = useModal();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const hasSelection = selection != null;
  const isSun = hasSelection && selection.kind === 'sun';
  const isPerson = hasSelection && selection.kind === 'person';

  function handleAddPerson() {
    onAddPerson(selection);
  }
  function handleAddProgram() {
    onAddProgram(hasSelection && selection.kind !== 'sun' && selection.gi != null ? selection.gi : 0);
  }

  function handleEditSelected() {
    if (!hasSelection) return;
    if (selection.kind === 'person') onEditPerson(selection);
    else if (selection.kind === 'prog') onEditProgram(selection);
    else if (selection.kind === 'group') onEditGroup(selection);
    else onEditLeadership();
  }

  function handleDuplicate() {
    if (!hasSelection || isSun) return;
    if (selection.kind === 'person') {
      dispatch({ type: 'DUPLICATE_PERSON', gi: selection.gi, pi: selection.pi, ki: selection.ki });
    } else if (selection.kind === 'prog') {
      dispatch({ type: 'DUPLICATE_PROGRAM', gi: selection.gi, pi: selection.pi });
    } else if (selection.kind === 'group') {
      dispatch({ type: 'DUPLICATE_GROUP', gi: selection.gi });
    }
    pbToast('Duplicated', 'ok');
  }

  function handleDelete() {
    if (!hasSelection || isSun) return;
    if (selection.kind === 'person') {
      const person = personAt(doc, selection);
      if (!person) return;
      const who = person.vacant ? `the vacant ${person.role} seat` : `${person.name} (${person.role})`;
      if (!confirm(`Remove ${who} from ${progLabel(doc, selection.gi, selection.pi)}?`)) return;
      dispatch({ type: 'DELETE_PERSON', gi: selection.gi, pi: selection.pi, ki: selection.ki });
      pbToast(`Removed ${person.vacant ? 'vacant seat' : person.name}`, 'ok');
    } else if (selection.kind === 'prog') {
      const group = doc.groups[selection.gi];
      const program = group && group.programs[selection.pi];
      if (!program) return;
      if (!confirm(`Delete the program "${program.short}" and its ${program.people.length} people?`)) return;
      if (group.programs.length === 1) {
        pbToast('A group needs at least one program — delete the group instead.', 'err');
        return;
      }
      dispatch({ type: 'DELETE_PROGRAM', gi: selection.gi, pi: selection.pi });
      pbToast(`Deleted program ${program.short}`, 'ok');
    } else if (selection.kind === 'group') {
      const group = doc.groups[selection.gi];
      if (!group) return;
      const peopleCount = group.programs.reduce((sum, program) => sum + program.people.length, 0);
      if (!confirm(`Delete the group "${group.short}", its ${group.programs.length} program(s) and ${peopleCount} people?`)) return;
      dispatch({ type: 'DELETE_GROUP', gi: selection.gi });
      pbToast(`Deleted group ${group.short}`, 'ok');
    }
  }

  function handleLabelModeChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value;
    dispatch({ type: 'UPDATE_SETTINGS', patch: { labelMode: value } });
    pbToast(`Default label style: ${value}`, 'ok');
  }

  function rotateLabel(delta: number) {
    if (!isPerson) return;
    const person = personAt(doc, selection);
    if (!person) return;
    dispatch({
      type: 'UPDATE_PERSON',
      gi: selection.gi,
      pi: selection.pi,
      ki: selection.ki,
      patch: { ...person, labelRot: (person.labelRot || 0) + delta },
    });
  }

  function handleAutoArrange() {
    if (!confirm('Clear every manual position and size, returning the chart to its automatic layout? People, programs and groups all stay — only their coordinates reset.')) return;
    dispatch({ type: 'AUTO_ARRANGE' });
    pbToast('Layout auto-arranged', 'ok');
  }

  function handleUndo() {
    if (!canUndo) return;
    dispatch({ type: 'UNDO' });
    pbToast('Reverted last change', 'ok');
  }

  function handleReset() {
    if (!confirm('Reset the chart to the original org structure? All edits will be lost.')) return;
    dispatch({ type: 'RESET_TO_BASELINE', baseline });
    pbToast('Chart reset to baseline', 'ok');
  }

  function handleExport() {
    const payload = {
      schemaVersion: doc.schemaVersion,
      org: doc.org,
      settings: doc.settings,
      sun: { x: doc.sun.x, y: doc.sun.y },
      groups: doc.groups,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'itpms-org-chart.json';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    pbToast('Exported itpms-org-chart.json', 'ok');
  }

  function handleImportClick() {
    if (fileInputRef.current) fileInputRef.current.click();
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    const file = input.files && input.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      let parsed: unknown;
      try {
        parsed = JSON.parse(String(reader.result));
      } catch (err) {
        const parseErr = err as Error;
        pbToast(`Could not read that file: ${parseErr.message}`, 'err');
        input.value = '';
        return;
      }
      const { ok, errors } = validateChartDoc(parsed);
      if (!ok) {
        pbToast(`Could not read that file: ${errors.join('; ')}`, 'err');
        input.value = '';
        return;
      }
      dispatch({ type: 'IMPORT_DOC', doc: parsed });
      pbToast(`Loaded ${file.name}`, 'ok');
      input.value = '';
    };
    reader.readAsText(file);
  }

  return (
    <>
      <div className="tbrow">
        <button
          type="button"
          className="tb primary"
          aria-pressed={editMode}
          onClick={() => dispatch({ type: 'SET_EDIT_MODE', on: !editMode })}
        >
          {editMode ? '✓ Done editing' : '✎ Edit chart'}
        </button>
        <span className="tb-sp" />
        <ZoomControls zoom={zoom} onZoom={(z) => dispatch({ type: 'SET_ZOOM', zoom: z })} svgRef={svgRef} />
      </div>

      {editMode && (
        <div className="tbrow">
          <span className="tbgrp">
            <span className="glbl">Add</span>
            <button type="button" className="tb" onClick={handleAddPerson}>+ Employee</button>
            <button type="button" className="tb" onClick={handleAddProgram}>+ Program</button>
            <button type="button" className="tb" onClick={() => onAddGroup()}>+ Group</button>
          </span>
          <span className="tbgrp">
            <span className="glbl">Selected</span>
            <button type="button" className="tb" disabled={!hasSelection} onClick={handleEditSelected}>Edit</button>
            <button type="button" className="tb" disabled={!hasSelection || isSun} onClick={handleDuplicate}>Duplicate</button>
            <button type="button" className="tb danger" disabled={!hasSelection || isSun} onClick={handleDelete}>Delete</button>
          </span>
          <span className="tbgrp">
            <span className="glbl">Labels</span>
            <select
              className="tbsel"
              aria-label="Default label style"
              value={doc.settings.labelMode}
              onChange={handleLabelModeChange}
            >
              <option value="auto">Side (auto)</option>
              <option value="radial">Radial spoke</option>
              <option value="tangent">Along orbit</option>
              <option value="above">Above dot</option>
              <option value="below">Below dot</option>
              <option value="hidden">Hidden</option>
            </select>
            <button
              type="button"
              className="tb"
              disabled={!isPerson}
              title="Rotate selected label −15°"
              aria-label="Rotate selected label −15°"
              onClick={() => rotateLabel(-15)}
            >↺</button>
            <button
              type="button"
              className="tb"
              disabled={!isPerson}
              title="Rotate selected label +15°"
              aria-label="Rotate selected label +15°"
              onClick={() => rotateLabel(15)}
            >↻</button>
          </span>
          <span className="tbgrp">
            <span className="glbl">Chart</span>
            <button type="button" className="tb" onClick={() => onSettings()}>⚙ Settings</button>
            <button type="button" className="tb" onClick={() => onEditLeadership()}>Leadership</button>
            <button type="button" className="tb" onClick={handleAutoArrange}>Auto-arrange</button>
          </span>
          <span className="tbgrp">
            <span className="glbl">File</span>
            <button type="button" className="tb" disabled={!canUndo} onClick={handleUndo}>↶ Undo</button>
            <button type="button" className="tb" onClick={handleReset}>Reset</button>
            <button type="button" className="tb" onClick={handleExport}>↓ JSON</button>
            <button type="button" className="tb" onClick={handleImportClick}>↑ Load</button>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json,.json"
              hidden
              onChange={handleImportFile}
            />
          </span>
        </div>
      )}
    </>
  );
}
